<template>
  <div
    id="announcement-dropdown"
    class="flex-align-items-center"
  >
    <v-dialog
      v-model="showModal"
      :max-width="765"
      content-class="modal-content"
    >
      <AnnouncementModal
        :announcement="selectedAnnouncement"
        :close-callback="closeModal"
      />
    </v-dialog>
    <v-menu
      v-model="menuOpen"
      data-app
      location="left"
      :max-height="`calc(100vh - 75px)`"
      :max-width="menuWidth"
      :min-width="menuWidth"
      class="account-menu announcement-window"
      :close-on-content-click="false"
    >
      <template #activator="{ props }">
        <a-btn
          v-bind="props"
          class="account-menu-button label-medium"
          :class="{
            'announcement-adjust-for-badge': hasUnalertedAnnouncements
          }"
          :color="constants.ENV_COLOR"
        >
          <template #default>
            <v-icon icon="mdi-bell" size="x-large"/>
            <v-badge
              v-if="hasUnalertedAnnouncements"
              dot
              class="alert-badge"
              color="error-lighten-1"
            />
          </template>
        </a-btn>
      </template>
      <div class="announcement-dropdown-menu">
        <v-list v-if="loadingAgain">
          <v-list-item class="pr-1">
            <template #prepend>
              <SpinnerInline
                :size="20"
                color="primary"
              />
            </template>
            Loading
          </v-list-item>
        </v-list>
        <v-list v-else-if="appStore.announcements?.length === 0">
          <v-list-item class="pr-1">
            You don't have any notifications
          </v-list-item>
        </v-list>
        <v-list v-else-if="appStore.announcements?.length > 0">
          <template
            v-for="(item, index) in appStore.announcements"
            :key="item.id"
          >
            <v-list-item class="pr-1">
              <div class="d-flex justify-space-between align-center w-100">
                <div class="d-flex align-center ga-3">
                  <v-icon
                    v-if="!item.read"
                    size="small"
                    tool
                    color="error-lighten-1"
                    class="text-body-1"
                  >
                    mdi-circle
                  </v-icon>
                  <span>{{ item.alertText }}</span>
                </div>
                <a-btn
                  v-if="item.expandable"
                  variant="text"
                  color="primary"
                  class="learn-more-btn text-transform-unset"
                  text="Learn More"
                  @click=";[(item.read = true), openModal(item)]"
                />
              </div>
            </v-list-item>
            <v-divider
              v-if="index < appStore.announcements.length - 1"
            />
          </template>
        </v-list>
      </div>
    </v-menu>
  </div>
</template>

<script setup>
import constants from '@/helpers/constants'

import SpinnerInline from '@/components/SpinnerInline'
import AnnouncementModal from '@/components/AnnouncementModal.vue'
import { getRequestWithParams } from '@/helpers/helpers'
import { useFileStore } from '@/stores/FileStore.js'

import { computed, ref, watch } from 'vue'
import { useUserStore } from '@/stores/UserStore.js'
import { useAppStore } from '@/stores/AppStore.js'

const appStore = useAppStore()
const userStore = useUserStore()
const fileStore = useFileStore()

const showModal = ref(false)
const headerColor = ref(constants.ENV_COLOR)
const menuOpen = ref(false)
const selectedAnnouncement = ref({})
const loadComplete = ref(false)
const loadingAgain = ref(true)

watch(menuOpen, (newValue, oldValue) => {
  if (newValue) {
    getActiveAnnouncementsAgain()
  }
})

const userId = computed(() => {
  return userStore.details.id
})
const menuWidth = computed(() => {
  return constants.IS_MOBILE ? 320 : 400
})
const hasUnalertedAnnouncements = computed(() => {
  if (Array.isArray(appStore.announcements)) {
    return (
      appStore?.announcements?.filter((a) => !a.alerted)?.length > 0 || false
    )
  } else {
    return false
  }
})

//we reload them again here in case something changes
const getActiveAnnouncementsAgain = async () => {
  try {
    loadingAgain.value = true
    const params = {
      doUpdate: true //we do this every time in case something changed behind the scenes
    }
    const { data } = await getRequestWithParams(`/announcements/active`, {
      params
    })
    appStore.announcements = data
  } catch (e) {
    console.error('*** ERROR ***', e)
    appStore.showSnack('ERROR', 'Error Retrieving Data')
  } finally {
    loadingAgain.value = false
  }
}
const openModal = async (item) => {
  menuOpen.value = false
  showModal.value = true
  loadComplete.value = false
  await getAttachment(item)
  selectedAnnouncement.value = item
}
const getAttachment = async (item) => {
  try {
    await fileStore.getOne({
      attachmentTypeId: 990,
      sourceId: item.id,
      callback: async (img) => {
        item.presignedUrl = img.presignedUrl
        item.attachmentId = img.id
        loadComplete.value = true
      }
    })
  } catch (e) {
    console.error('*** ERROR ***', e)
    loadComplete.value = true
  }
}
const closeModal = () => {
  showModal.value = false
  selectedAnnouncement.value = {}
}
</script>

<style lang="scss">
.announcement-adjust-for-badge .v-btn__content {
  padding-right: 8px;
}
</style>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.announcement-action {
  width: 100px;
  justify-content: center;
  margin: 0 !important;
}
.announcement-window :deep(.v-overlay__content) {
  top: 37px !important;
  right: 93px !important;
  left: auto !important;
}
@media (max-width: 600px) {
  .announcement-window :deep(.v-overlay__content) {
    right: 13px !important;
  }
}

.account-menu-button {
  text-transform: capitalize;
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
  border: none !important;
}

.alert-badge {
  margin-left: -2px;
  margin-bottom: 14px;
}

.learn-more-btn {
  padding-left: 4px !important;
  padding-right: 16px !important;
}
.announcement-dropdown-menu :deep(.v-list) {
  box-shadow: rgba(0, 0, 0, 0.2) 0px 5px 5px -3px, rgba(0, 0, 0, 0.14) 0px 8px 10px 1px, rgba(0, 0, 0, 0.12) 0px 3px 14px 2px;
  border-radius: 4px;
}
</style>
